import { Fragment, useEffect } from 'react';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import { faSquareRss } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Skeleton, Stack, useCanonicalLayout } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '~/components/atoms/AppIcon';
import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { Thumbnail } from '~/components/atoms/Thumbnail';
import { Heading } from '~/components/molecules/Heading';
import { useAppPreviewDispatch } from '~/components/organisms/AppPreview';
import { useAppPreviewState } from '~/components/organisms/AppPreview/useAppPreviewState';
import { ArticleList } from '~/components/organisms/ArticleList';
import { CategoryBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { CategoryList } from '~/components/organisms/CategoryList';
import { useNavigate } from '~/components/utils/Navigation';
import { findPath } from '~/helpers/tree';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useCategoriesQuery } from '~/lib/api/modules/Categories/hooks/useCategoriesQuery';

export type IAppCategoryPageProps = {
  appId: string;
  categoryId?: string;
};

export const AppCategoryPage: React.FC<IAppCategoryPageProps> = (props) => {
  const { appId, categoryId } = props;
  const { t } = useTranslation();

  const appPreviewDispatch = useAppPreviewDispatch();
  const appPreviewState = useAppPreviewState();
  const navigate = useNavigate();
  const appQuery = useAppQuery({ appId });
  const categoriesQuery = useCategoriesQuery({ appId });

  useEffect(() => {
    // Delay preview bootstrapping until the page critical data is loaded.
    // This avoids a burst of preview iframe requests right after login,
    // while the dashboard is still restoring app/category state.
    if (
      !appPreviewState.opened ||
      !appQuery.isSuccess ||
      !categoriesQuery.isSuccess
    ) {
      return;
    }

    appPreviewDispatch.navigateToRoute({
      appId,
      path: categoryId ? `/categories/${categoryId}` : '/',
      tab: 'categories',
    });
  }, [
    appId,
    categoryId,
    appPreviewDispatch,
    appPreviewState.opened,
    appQuery.isSuccess,
    categoriesQuery.isSuccess,
  ]);

  const currentCategory = categoriesQuery.data?.categories?.find(
    (category) => category._id === categoryId,
  );
  const parentCategoryId = currentCategory?.parentId;

  useEffect(() => {
    const isLink = !!currentCategory?.action;
    if (isLink) {
      navigate(
        parentCategoryId
          ? `/apps/${appId}/categories/${parentCategoryId}`
          : `/apps/${appId}/content`,
      );
    }
  }, [currentCategory?.action, navigate, appId, parentCategoryId]);

  const path =
    categoriesQuery.data?.categories && categoryId
      ? findPath(categoriesQuery.data.categories, categoryId)
      : undefined;

  const thumbnailUrl = currentCategory?.picture?.[0]?.mediumUrl;
  const pageNameQuery = path?.length ? categoriesQuery : appQuery;
  const categoryName = currentCategory?.name;
  const appName = appQuery.data?.name;
  const pageName = categoryName ?? appName;
  const pageTitle = categoryName ?? t('AppCategoryPage.seo.title');

  const canonicalLayout = useCanonicalLayout('listDetail');
  const horizontal = canonicalLayout.orientation === 'horizontal';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {categoryId && (
        <CategoryBreadcrumbs appId={appId} categoryId={categoryId} />
      )}

      <Heading
        icon={
          <Skeleton loaded={!appQuery.isLoading}>
            {thumbnailUrl ? (
              <Thumbnail url={thumbnailUrl} />
            ) : (
              <AppIcon app={appQuery.data} />
            )}
          </Skeleton>
        }
        previousUrl={
          path?.length
            ? path.length > 1
              ? `/apps/${appId}/categories/${path[1]._id}`
              : `/apps/${appId}/content`
            : undefined
        }
        title={
          <QuerySkeleton query={pageNameQuery} length={{ min: 8, max: 16 }}>
            {currentCategory?.rssFeedUrl && (
              <>
                <FontAwesomeIcon icon={faSquareRss} />{' '}
              </>
            )}
            {pageName}
          </QuerySkeleton>
        }
        titleAction={
          categoryId && (
            <IconButton
              href={`/apps/${appId}/categories/${categoryId}/update`}
              icon={<FontAwesomeIcon icon={faPen} />}
            />
          )
        }
      />

      <Stack horizontal={horizontal} gap={6} align="start">
        {canonicalLayout.panes.map((pane) => (
          <Fragment key={pane.name}>
            {pane.name === 'list' ? (
              <CategoryList appId={appId} parentId={categoryId} grow />
            ) : pane.name === 'detail' ? (
              <ArticleList appId={appId} categoryId={categoryId} grow />
            ) : pane.name === 'listDetail' ? (
              <Stack gap={6} grow>
                <CategoryList appId={appId} parentId={categoryId} />
                <ArticleList appId={appId} categoryId={categoryId} />
              </Stack>
            ) : null}
          </Fragment>
        ))}
      </Stack>
    </>
  );
};
